import {
  Box,
  Flex,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import { HelperTooltip } from 'components/Tooltip';
import PropTypes from 'prop-types';
import { fmtIntegerInput, parseIntegerInput } from './calc/formatters';
import { resolveFundingCommitmentMonths } from './calc/resolveFundingCommitment';
import { PRO_RATA_REV_SHARE_TOOLTIP } from './calc/resolvePlanFunding';
import { isRevSharePctConfigured } from './calc/resolveProRataRevShare';
import { resolveDealIrrHillK, DEAL_IRR_COGS_TOOLTIP } from './calc/resolveDealIrrModelInputs';
import { HILL_PLAN_MONTH_LEARNING_TOOLTIP } from './calc/hillPlanMonthLearning';
import {
  DEFAULT_FUNDING_PCT,
  DEFAULT_MAX_EXPOSURE,
  FACILITY_SIZE_TOOLTIP,
  DEFAULT_REV_SHARE_PCT,
  MAX_FACILITY_FEE_INSTALLMENTS,
} from './constants';
import {
  resolveFacilitySize,
  resolveRequiredMaxExposure,
  stateWithMaxExposure,
} from './calc/resolvePlanCovenantLimits';
import { MARKETING_SPEND_GRACE_TOOLTIP, REPAYMENT_GRACE_TOOLTIP } from './FacilityGracePeriodsRow';
import { resolveFacilityFeeStartMonth } from './calc/resolveDealIrrModelInputs';

const HILL_K_TOOLTIP =
  'Hill saturation constant (K). Changes spend-based saturation K/(K+spend) and revenue at each spend level. Does not change the Max ROAS curve.';

const DEAL_IRR_LEARNING_FACTOR_TOOLTIP = HILL_PLAN_MONTH_LEARNING_TOOLTIP;

const COMPACT_INPUT_PROPS = {
  size: 'xs',
  h: '24px',
  fontSize: 'xs',
  px: 2,
};

const MAX_EXPOSURE_TOOLTIP =
  'Required. Ending Twelve balance must not exceed this amount at any month. Default is $1,000,000. Changing this also fills facility size.';

const MAX_MONTHLY_DRAW_TOOLTIP =
  'Optional. Maximum Twelve draw for each cohort funding month.';

const MAX_DRAW_INCREASE_TOOLTIP =
  'Optional. Maximum month-over-month increase in draw vs the prior month draw.';

const FUNDING_CAP_TOOLTIP =
  'Maximum share of marketing spend Twelve will fund each month (Funding Cap %).';

const COMMITMENT_TOOLTIP =
  'Number of plan months Twelve will fund, counting from the first plan month.';

function CompactField({ label, tooltip, children, inputWidth = '84px' }) {
  return (
    <Flex align="center" gap={1} minH="24px">
      <Flex align="center" gap={0} flexShrink={1} minW={0}>
        <Text fontSize="xs" color="gray.600" noOfLines={2} lineHeight="short">
          {label}
        </Text>
        {tooltip ? <HelperTooltip content={tooltip} /> : null}
      </Flex>
      <Box flexShrink={0} w={inputWidth}>
        {children}
      </Box>
    </Flex>
  );
}

CompactField.propTypes = {
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.node,
  children: PropTypes.node.isRequired,
  inputWidth: PropTypes.string,
};

function DealIrrModelEditInputs({
  state,
  setState,
  planMonths,
  defaultRevenueSharePct,
  facilityFeeInstallmentCount,
  useHillRoas,
  hillModel,
}) {
  const planMarketingMonthCount = state.marketing?.length ?? planMonths.length;
  const resolvedHillK = resolveDealIrrHillK(state, hillModel);
  const showHillInputs = Boolean(useHillRoas && resolvedHillK != null);

  return (
    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
      <Stack spacing={1}>
        <CompactField label="Facility size ($)" tooltip={FACILITY_SIZE_TOOLTIP}>
          <Input
            {...COMPACT_INPUT_PROPS}
            aria-label="Facility size ($)"
            type="text"
            inputMode="numeric"
            textAlign="right"
            value={fmtIntegerInput(resolveFacilitySize(state))}
            onChange={(e) => {
              const raw = e.target.value.replace(/,/g, '').trim();
              const parsed = Math.max(0, parseIntegerInput(raw));
              setState((s) => ({
                ...s,
                facilitySize: parsed > 0 ? parsed : 0,
              }));
            }}
          />
        </CompactField>
        <CompactField label="Facility fee (%)">
          <Input
            {...COMPACT_INPUT_PROPS}
            type="number"
            textAlign="right"
            value={state.facilityFeePct}
            onChange={(e) =>
              setState((s) => ({
                ...s,
                facilityFeePct: Number(e.target.value) || 0,
              }))
            }
          />
        </CompactField>
        <CompactField label="Fee installments">
          <Select
            {...COMPACT_INPUT_PROPS}
            value={String(facilityFeeInstallmentCount)}
            onChange={(e) =>
              setState((s) => ({
                ...s,
                facilityFeeInstallments: Math.max(
                  1,
                  Math.min(
                    MAX_FACILITY_FEE_INSTALLMENTS,
                    Number(e.target.value)
                  )
                ),
              }))
            }
          >
            {Array.from(
              { length: MAX_FACILITY_FEE_INSTALLMENTS },
              (_, i) => i + 1
            ).map((count) => (
              <option key={count} value={count}>
                {count}
              </option>
            ))}
          </Select>
        </CompactField>
        <CompactField label="Fee first payment" inputWidth="96px">
          <Select
            {...COMPACT_INPUT_PROPS}
            value={String(resolveFacilityFeeStartMonth(state))}
            onChange={(e) =>
              setState((s) => ({
                ...s,
                facilityFeeFirstPaymentMonth: Number(e.target.value),
              }))
            }
          >
            {planMonths.map((_, index) => (
              <option key={index} value={index}>
                M{index}
              </option>
            ))}
          </Select>
        </CompactField>
        <CompactField
          label="Commitment period (mo)"
          tooltip={COMMITMENT_TOOLTIP}
        >
          <Input
            {...COMPACT_INPUT_PROPS}
            type="number"
            min={1}
            max={planMarketingMonthCount}
            textAlign="right"
            aria-label="Commitment period (months)"
            value={
              state.commitmentMonths == null || state.commitmentMonths === ''
                ? planMarketingMonthCount
                : state.commitmentMonths
            }
            onChange={(e) => {
              const raw = e.target.value.trim();
              setState((s) => {
                const planLength = s.marketing?.length ?? planMarketingMonthCount;
                if (raw === '') {
                  return { ...s, commitmentMonths: null };
                }
                const val = Math.max(
                  1,
                  Math.min(planLength, Math.floor(Number(raw) || 1))
                );
                return {
                  ...s,
                  commitmentMonths: val >= planLength ? null : val,
                };
              });
            }}
          />
        </CompactField>
      </Stack>

      <Stack spacing={1}>
        <CompactField label="Funding Cap %" tooltip={FUNDING_CAP_TOOLTIP}>
          <Input
            {...COMPACT_INPUT_PROPS}
            type="number"
            textAlign="right"
            aria-label="Funding cap percent"
            value={state.defaultFundingPct ?? DEFAULT_FUNDING_PCT}
            onChange={(e) => {
              const val = Number(e.target.value) || 0;
              setState((s) => {
                const limit = resolveFundingCommitmentMonths(s);
                return {
                  ...s,
                  defaultFundingPct: val,
                  fundingPct: (s.fundingPct || []).map((pct, i) =>
                    i < limit ? val : pct
                  ),
                };
              });
            }}
          />
        </CompactField>
        <CompactField label="COGS (%)" tooltip={DEAL_IRR_COGS_TOOLTIP}>
          <Input
            {...COMPACT_INPUT_PROPS}
            type="number"
            min={0}
            max={100}
            textAlign="right"
            aria-label="COGS percent"
            value={defaultRevenueSharePct}
            onChange={(e) =>
              setState((s) => ({
                ...s,
                revenueShareForRepaymentPct: Number(e.target.value) || 0,
              }))
            }
          />
        </CompactField>
        <CompactField label="Revenue share %" tooltip={PRO_RATA_REV_SHARE_TOOLTIP}>
          <Input
            {...COMPACT_INPUT_PROPS}
            type="number"
            min={0}
            max={100}
            textAlign="right"
            placeholder="Pro-rata"
            aria-label="Revenue share percent"
            value={
              isRevSharePctConfigured(state.defaultRevSharePct)
                ? state.defaultRevSharePct
                : ''
            }
            onChange={(e) => {
              const raw = e.target.value.trim();
              setState((s) => {
                const limit = resolveFundingCommitmentMonths(s);
                if (raw === '') {
                  return {
                    ...s,
                    defaultRevSharePct: null,
                    revSharePct: (s.revSharePct || []).map((pct, i) =>
                      i < limit ? null : pct
                    ),
                  };
                }
                const val = Number(raw) || 0;
                return {
                  ...s,
                  defaultRevSharePct: val,
                  revSharePct: (s.revSharePct || []).map((pct, i) =>
                    i < limit ? val : pct
                  ),
                };
              });
            }}
          />
        </CompactField>
        <CompactField
          label="Marketing spend grace"
          tooltip={MARKETING_SPEND_GRACE_TOOLTIP}
          inputWidth="96px"
        >
          <Select
            {...COMPACT_INPUT_PROPS}
            aria-label="Marketing spend grace (months)"
            value={String(state.marketingGracePeriod ?? 1)}
            onChange={(e) =>
              setState((s) => ({
                ...s,
                marketingGracePeriod: Number(e.target.value),
              }))
            }
          >
            <option value="0">0</option>
            <option value="1">1</option>
          </Select>
        </CompactField>
        <CompactField
          label="Repayment grace (mo)"
          tooltip={REPAYMENT_GRACE_TOOLTIP}
        >
          <Input
            {...COMPACT_INPUT_PROPS}
            aria-label="Repayment grace period (months)"
            type="number"
            textAlign="right"
            value={state.gracePeriod}
            onChange={(e) =>
              setState((s) => ({
                ...s,
                gracePeriod: Number(e.target.value) || 0,
              }))
            }
          />
        </CompactField>
      </Stack>

      <Stack spacing={1}>
        <CompactField label="Max Exposure ($)" tooltip={MAX_EXPOSURE_TOOLTIP}>
          <Input
            {...COMPACT_INPUT_PROPS}
            type="text"
            inputMode="numeric"
            textAlign="right"
            aria-label="Max exposure ($)"
            value={fmtIntegerInput(resolveRequiredMaxExposure(state.maxExposure))}
            onChange={(e) => {
              const raw = e.target.value.replace(/,/g, '').trim();
              const parsed = Math.max(0, parseIntegerInput(raw));
              const maxExposure = parsed > 0 ? parsed : DEFAULT_MAX_EXPOSURE;
              setState((s) => stateWithMaxExposure(s, maxExposure));
            }}
          />
        </CompactField>
        <CompactField
          label="Max monthly draw ($)"
          tooltip={MAX_MONTHLY_DRAW_TOOLTIP}
        >
          <Input
            {...COMPACT_INPUT_PROPS}
            type="text"
            inputMode="numeric"
            textAlign="right"
            placeholder="—"
            value={
              state.maxMonthlyDraw != null
                ? fmtIntegerInput(state.maxMonthlyDraw)
                : ''
            }
            onChange={(e) => {
              const raw = e.target.value.replace(/,/g, '').trim();
              setState((s) => ({
                ...s,
                maxMonthlyDraw:
                  raw === '' ? null : Math.max(0, parseIntegerInput(raw)),
              }));
            }}
          />
        </CompactField>
        <CompactField
          label="Max draw increase %"
          tooltip={MAX_DRAW_INCREASE_TOOLTIP}
        >
          <Input
            {...COMPACT_INPUT_PROPS}
            type="number"
            min={0}
            textAlign="right"
            placeholder="—"
            value={state.maxDrawIncreasePct ?? ''}
            onChange={(e) => {
              const raw = e.target.value.trim();
              setState((s) => ({
                ...s,
                maxDrawIncreasePct:
                  raw === '' ? null : Math.max(0, Number(raw) || 0),
              }));
            }}
          />
        </CompactField>
        {showHillInputs ? (
          <>
            <CompactField label="K ($)" tooltip={HILL_K_TOOLTIP}>
              <Input
                {...COMPACT_INPUT_PROPS}
                type="text"
                inputMode="numeric"
                textAlign="right"
                aria-label="Hill K value"
                value={fmtIntegerInput(resolvedHillK)}
                onChange={(e) =>
                  setState((s) => ({
                    ...s,
                    dealIrrHillK: Math.max(0, parseIntegerInput(e.target.value)),
                  }))
                }
              />
            </CompactField>
            <CompactField
              label="Learning factor"
              tooltip={DEAL_IRR_LEARNING_FACTOR_TOOLTIP}
            >
              <Input
                {...COMPACT_INPUT_PROPS}
                type="number"
                min={0}
                step={0.001}
                textAlign="right"
                aria-label="Deal IRR learning factor"
                value={state.dealIrrLearningFactor ?? 0.01}
                onChange={(e) => {
                  const raw = e.target.value.trim();
                  setState((s) => ({
                    ...s,
                    dealIrrLearningFactor:
                      raw === '' ? 0 : Math.max(0, Number(raw) || 0),
                  }));
                }}
              />
            </CompactField>
          </>
        ) : (
          <Text fontSize="xs" color="gray.500" lineHeight="short">
            Edit cumulative ROAS in the table below.
          </Text>
        )}
      </Stack>
    </SimpleGrid>
  );
}

DealIrrModelEditInputs.propTypes = {
  state: PropTypes.object.isRequired,
  setState: PropTypes.func.isRequired,
  planMonths: PropTypes.array.isRequired,
  defaultRevenueSharePct: PropTypes.number.isRequired,
  facilityFeeInstallmentCount: PropTypes.number.isRequired,
  useHillRoas: PropTypes.bool,
  hillModel: PropTypes.object,
};

export default DealIrrModelEditInputs;
